import React from 'react';
import { Box, Link as MuiLink } from '@mui/material';
import { styled } from '@mui/system';

const FooterContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#030C1E',
  padding: '80px 120px',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 10,
  '@media (max-width: 1600px)': {
    padding: '70px 100px',
  },
  '@media (max-width: 1440px)': {
    padding: '60px 80px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '60px 40px',
    gap: '60px',
  },
}));

const MainContentArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '60px',
  gap: '80px',
  '@media (max-width: 1600px)': {
    gap: '64px',
    marginBottom: '50px',
  },
  '@media (max-width: 1440px)': {
    gap: '48px',
    marginBottom: '40px',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '60px',
    marginBottom: '40px',
  },
}));

const LeftSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  '@media (max-width: 1600px)': {
    gap: '28px',
  },
  '@media (max-width: 1440px)': {
    gap: '24px',
  },
});

const CompanyBlock = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  '@media (max-width: 1600px)': {
    gap: '20px',
  },
  '@media (max-width: 1440px)': {
    gap: '18px',
  },
});

const Logo = styled('div')({
  fontFamily: 'Arial, sans-serif',
  fontSize: '24px',
  fontWeight: 'bold',
  letterSpacing: '0.1rem',
  color: '#FFFFFF',
  '@media (max-width: 1440px)': {
    fontSize: '22px',
  },
});

const CompanyText = styled('div')({
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: '15px',
  lineHeight: '1.5',
  color: '#FFFFFF',
  letterSpacing: '0.15px',
  whiteSpace: 'pre-line',
  '@media (max-width: 1600px)': {
    fontSize: '14px',
  },
  '@media (max-width: 1440px)': {
    fontSize: '13px',
  },
});

const CompanyInfoBlock = styled(Box)({
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: '15px',
  lineHeight: '1.8',
  color: '#FFFFFF',
  letterSpacing: '0.15px',
  whiteSpace: 'pre-line',
  marginTop: '8px',
  '@media (max-width: 1600px)': {
    fontSize: '14px',
    marginTop: '6px',
  },
  '@media (max-width: 1440px)': {
    fontSize: '13px',
    marginTop: '4px',
  },
});

const RightSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  alignItems: 'flex-end',
  [theme.breakpoints.down('md')]: {
    alignItems: 'flex-start',
    width: '100%',
  },
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '61px',
  alignItems: 'center',
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: '15px',
  fontWeight: 600,
  color: '#FFFFFF',
  letterSpacing: '0.15px',
  '@media (max-width: 1600px)': {
    gap: '48px',
    fontSize: '14px',
  },
  '@media (max-width: 1440px)': {
    gap: '40px',
    fontSize: '13px',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: '20px',
    alignItems: 'flex-start',
  },
}));

const NavLink = styled(MuiLink)({
  color: '#FFFFFF',
  textDecoration: 'none',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
});

const BottomSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  paddingTop: '40px',
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  '@media (max-width: 1600px)': {
    paddingTop: '35px',
  },
  '@media (max-width: 1440px)': {
    paddingTop: '30px',
  },
  '@media (max-width: 768px)': {
    paddingTop: '30px',
  },
});

const CopyrightText = styled('div')({
  fontFamily: "'Noto Sans JP', sans-serif",
  fontSize: '15px',
  lineHeight: '1.3',
  color: '#FFFFFF',
  opacity: 0.5,
  letterSpacing: '0.15px',
  '@media (max-width: 1600px)': {
    fontSize: '14px',
  },
  '@media (max-width: 1440px)': {
    fontSize: '13px',
  },
});

const FooterSection = () => {
  const handleScrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <FooterContainer>
      <MainContentArea>
        <LeftSection>
          <CompanyBlock>
            <Logo>STELLA</Logo>
          </CompanyBlock>
          <CompanyInfoBlock>
            会社情報{'\n'}
            社名：STELLA, Inc.{'\n'}
            営業所：〒105-0001 東京都港区虎ノ門２丁目２−１ 5F{'\n'}
            設立：2019年1月30日{'\n'}
            資本金：1.16億円(準備金含む){'\n'}
            代表：福吉 真之
          </CompanyInfoBlock>
        </LeftSection>
        <RightSection>
          <NavLinks>
            <NavLink
              onClick={(e) => {
                e.preventDefault();
                handleScrollToSection('about');
              }}
            >
              わたしたちについて
            </NavLink>
            <NavLink
              onClick={(e) => {
                e.preventDefault();
                handleScrollToSection('projects');
              }}
            >
              プロダクト
            </NavLink>
            <NavLink
              onClick={(e) => {
                e.preventDefault();
                handleScrollToSection('news');
              }}
            >
              ニュース
            </NavLink>
            <NavLink
              onClick={(e) => {
                e.preventDefault();
                handleScrollToSection('contact');
              }}
            >
              お問い合わせ
            </NavLink>
            {/* 自社サービス Narrative への被リンク（nofollow は付けない） */}
            <NavLink href="https://makenarrative.com/" target="_blank" rel="noopener">
              Narrative
            </NavLink>
          </NavLinks>
        </RightSection>
      </MainContentArea>
      <BottomSection>
        <CopyrightText>
          All Rights Reserved. 2025 STELLA, Inc.
        </CopyrightText>
      </BottomSection>
    </FooterContainer>
  );
};

export default FooterSection;
